import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import BadgeIcon from '@mui/icons-material/Badge';
import SchoolIcon from '@mui/icons-material/School';
import useProfileOverview from '../hooks/useProfileOverview';

const formatDateTime = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '—';
  }
  return date.toLocaleString();
};

const CohortOverviewPage = () => {
  const { overview, loading, error, refresh } = useProfileOverview();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Unable to load cohort hub</AlertTitle>
          {error.response?.data?.message || error.message}
        </Alert>
        <Button variant="contained" onClick={refresh}>Retry</Button>
      </Container>
    );
  }

  const cohorts = overview?.cohorts || [];
  const tenant = overview?.tenant;
  const licenseSummary = overview?.licenses?.summary;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, color: '#146C94' }}>
        Cohort hub
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Track your cohort membership, coaching assignments, and licence usage.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper variant="outlined" sx={{ p: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
              <GroupsIcon color="primary" />
              <Typography variant="h6">Assigned cohorts</Typography>
            </Stack>
            <Stack spacing={2}>
              {cohorts.map((cohort) => (
                <Paper key={cohort.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{cohort.name}</Typography>
                      {cohort.tags?.map((tag) => (
                        <Chip key={tag} label={tag} size="small" />
                      ))}
                    </Stack>
                    {cohort.description && (
                      <Typography variant="body2" color="text.secondary">{cohort.description}</Typography>
                    )}
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Coach</Typography>
                        <Typography variant="body2">{cohort.coach?.name || 'To be assigned'}</Typography>
                        {cohort.coach?.email && (
                          <Typography variant="body2" color="text.secondary">{cohort.coach.email}</Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="caption" color="text.secondary">Schedule</Typography>
                        <Typography variant="body2">
                          {cohort.schedule?.startDate
                            ? `Starts ${formatDateTime(cohort.schedule.startDate)}`
                            : 'Start date pending'}
                        </Typography>
                        {cohort.schedule?.cadence && (
                          <Typography variant="body2" color="text.secondary">Cadence: {cohort.schedule.cadence}</Typography>
                        )}
                      </Grid>
                    </Grid>
                    {cohort.license && (
                      <Chip
                        icon={<BadgeIcon fontSize="small" />}
                        label={`Licence: ${cohort.license.name} (${cohort.license.status})`}
                        size="small"
                      />
                    )}
                  </Stack>
                </Paper>
              ))}
              {cohorts.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  You are not currently assigned to any cohorts. Please contact your administrator if this is unexpected.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Stack spacing={3}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
                <SchoolIcon color="primary" />
                <Typography variant="h6">University details</Typography>
              </Stack>
              {tenant ? (
                <Stack spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{tenant.name}</Typography>
                  <Typography variant="body2" color="text.secondary">Contact: {tenant.contact?.email || 'Not provided'}</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  University information will appear once an administrator links your account.
                </Typography>
              )}
            </Paper>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>Licence usage</Typography>
              {licenseSummary ? (
                <Stack spacing={1}>
                  <Typography variant="body2">Active licences: {licenseSummary.activeLicenses} / {licenseSummary.totalLicenses}</Typography>
                  <Typography variant="body2">Seats allocated: {licenseSummary.seatsAllocated} of {licenseSummary.seatLimit || '∞'}</Typography>
                  <Typography variant="body2">Next expiry: {licenseSummary.nearestExpiry ? formatDateTime(licenseSummary.nearestExpiry) : '—'}</Typography>
                </Stack>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No licence summary available yet.
                </Typography>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CohortOverviewPage;
